import React, { useCallback, useEffect, useMemo, useState } from "react";
import Input from "../../components/Atomos/Inputs/Input";
import InputLabel from "../../components/Atomos/Inputs/inputLabel";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import Modal from "../../components/Atomos/Modals/Modal";
import "../../assets/css/Text.css";
import "../../assets/css/InputText.css";
import {
  createEmailSenderUsername,
  deleteEmailSenderUsername,
  listEmailSenderUsernames,
  toSenderDropdownOptions,
  updateEmailSenderUsername,
} from "../../Services/EmailSenderUsername";
import { EmailSenderDropdownOption } from "../../interfaces/models/IEmailSenderUsername";
import { toastError, toastPromise, toastSuccess, toastWarn } from "../../utils/toast";
import FromEmailSenderDropdown from "./FromEmailSenderDropdown";

const FALLBACK_OPTIONS: EmailSenderDropdownOption[] = [
  { id: "alerts@worldservicesgroup.com", name: "alerts@worldservicesgroup.com", sender: "WSG Alerts", isCustom: false },
  { id: "events@worldservicesgroup.com", name: "events@worldservicesgroup.com", sender: "WSG Events", isCustom: false },
  { id: "support@worldservicesgroup.com", name: "support@worldservicesgroup.com", sender: "WSG Support", isCustom: false },
  { id: "accounting@worldservicesgroup.com", name: "accounting@worldservicesgroup.com", sender: "WSG Accounting Team", isCustom: false },
  { id: "DoNotReply@worldservicesgroup.com", name: "DoNotReply@worldservicesgroup.com", sender: "WSG DoNotReply", isCustom: false },
];

const DEFAULT_DOMAIN = "worldservicesgroup.com";
const USERNAME_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/;

type FormMode = "select" | "create" | "edit";

export type CustomFromEmailSelection = {
  fromEmail: string;
  sender: string;
};

type CustomFromEmailFieldsProps = {
  fromEmail: string;
  sender: string;
  errors?: { fromEmail?: string; sender?: string };
  onChange: (next: CustomFromEmailSelection) => void;
  onClearErrors?: (fields?: Array<"fromEmail" | "sender">) => void;
  /** Layout: envelope card uses 4-col grid; add envelope uses 6/4. */
  layout?: "envelope" | "add";
  trailingFields?: React.ReactNode;
};

function localPartFromEmail (email: string, domain: string): string | null {
  const trimmed = String(email || "").trim();
  const match = trimmed.match(/^([^@]+)@(.+)$/i);
  if (!match) {
    return null;
  }
  if (match[2].trim().toLowerCase() !== domain.toLowerCase()) {
    return null;
  }
  return match[1].trim();
}

function stripSpaces (value: string): string {
  return value.replace(/\s/g, "");
}

function sanitizeLocalPart (value: string): string {
  return stripSpaces(value).replace(/@.*$/, "");
}

type SenderEmailLocalPartInputProps = {
  title: string;
  name: string;
  value: string;
  domainName: string;
  onChange: (value: string) => void;
  error?: boolean;
  errorMsj?: string;
  required?: boolean;
  disabled?: boolean;
};

function SenderEmailLocalPartInput ({
  title,
  name,
  value,
  domainName,
  onChange,
  error = false,
  errorMsj,
  required = false,
  disabled = false,
}: SenderEmailLocalPartInputProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 md:gap-[.625rem] w-full h-full">
      <div className="flex gap-2.5 w-full">
        <InputLabel name={name} title={title} required={required} disabled={disabled} />
      </div>
      <div className="relative flex w-full items-stretch">
        <input
          type="text"
          id={name}
          name={name}
          value={value}
          onChange={(event) => onChange(sanitizeLocalPart(event.target.value))}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          disabled={disabled}
          className={`flex-1 min-w-0 rounded-r-none border-r-0 ${error ? "errorInput text-primary" : "text-input-gray"
            }`}
          placeholder="e.g. sramsey"
        />
        <span
          className={`flex shrink-0 items-center border border-solid border-l-0 border-gray-25 font-sans text-base md:text-lg not-italic font-normal leading-5 py-[.375rem] md:py-3 px-2 md:px-4 ${disabled
            ? "border-none bg-gray-100 text-gray-400 outline outline-1 outline-offset-0 outline-gray-300"
            : "bg-gray-25 text-input-gray"
            }`}
        >
          @{domainName}
        </span>
      </div>
      {error && errorMsj ? (
        <span className="flex flex-wrap items-center text-base font-medium text-primary">
          {errorMsj}
        </span>
      ) : null}
    </div>
  );
}

export default function CustomFromEmailFields ({
  fromEmail,
  sender,
  errors,
  onChange,
  onClearErrors,
  layout = "add",
  trailingFields,
}: CustomFromEmailFieldsProps): React.ReactElement {
  const [options, setOptions] = useState<EmailSenderDropdownOption[]>(FALLBACK_OPTIONS);
  const [domainName, setDomainName] = useState(DEFAULT_DOMAIN);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");

  const [formMode, setFormMode] = useState<FormMode>("select");
  const [draftLocalPart, setDraftLocalPart] = useState("");
  const [draftDisplayName, setDraftDisplayName] = useState("");
  const [editingOriginalEmail, setEditingOriginalEmail] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ localPart?: string; displayName?: string }>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmailSenderDropdownOption | null>(null);

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const selectionSnapshotRef = React.useRef<CustomFromEmailSelection | null>(null);

  const refreshSenders = useCallback(async (preferEmail?: string) => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await listEmailSenderUsernames();
      const nextOptions = toSenderDropdownOptions(response.items);
      setOptions(nextOptions.length > 0 ? nextOptions : FALLBACK_OPTIONS);
      setDomainName(response.domainName || DEFAULT_DOMAIN);
      setNotice(response.notice || "");

      if (preferEmail) {
        const match = nextOptions.find((item) => item.id === preferEmail);
        if (match) {
          onChangeRef.current({ fromEmail: match.id, sender: match.sender });
        }
      }
    } catch (error: any) {
      setLoadError(error?.message || "Could not load senders from Azure.");
      setOptions(FALLBACK_OPTIONS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSenders();
  }, [refreshSenders]);

  const isEditingForm = formMode === "create" || formMode === "edit";

  const existingSenderEmails = useMemo(
    () => new Set(options.map((option) => option.id.trim().toLowerCase())),
    [options],
  );

  const duplicateLocalPartError = useMemo(() => {
    if (formMode !== "create") {
      return undefined;
    }
    const local = draftLocalPart.trim();
    if (!local) {
      return undefined;
    }
    const emailAddress = `${local}@${domainName}`.toLowerCase();
    if (existingSenderEmails.has(emailAddress)) {
      return "This from email already exists. Select it from the list or choose a different username.";
    }
    return undefined;
  }, [domainName, draftLocalPart, existingSenderEmails, formMode]);

  const exitForm = useCallback((restoreSnapshot = false) => {
    if (restoreSnapshot && selectionSnapshotRef.current) {
      onChangeRef.current(selectionSnapshotRef.current);
    }
    selectionSnapshotRef.current = null;
    setFormMode("select");
    setDraftLocalPart("");
    setDraftDisplayName("");
    setEditingOriginalEmail(null);
    setFormErrors({});
  }, []);

  const syncDraftToParent = useCallback(
    (localPart: string, displayName: string) => {
      const nextEmail = localPart.trim()
        ? `${localPart.trim()}@${domainName}`
        : "";
      onChange({ fromEmail: nextEmail, sender: displayName });
    },
    [domainName, onChange],
  );

  const handleSelectFromEmail = (option: EmailSenderDropdownOption) => {
    if (!option.id) {
      onChange({ fromEmail: "", sender: "" });
      onClearErrors?.(["fromEmail", "sender"]);
      return;
    }
    onChange({ fromEmail: option.id, sender: option.sender });
    onClearErrors?.(["fromEmail", "sender"]);
    exitForm();
  };

  const handleStartCreate = () => {
    selectionSnapshotRef.current = { fromEmail, sender };
    setFormMode("create");
    setEditingOriginalEmail(null);
    setDraftLocalPart("");
    setDraftDisplayName("");
    setFormErrors({});
    onChange({ fromEmail: "", sender: "" });
    onClearErrors?.(["fromEmail", "sender"]);
  };

  const handleStartEdit = (option: EmailSenderDropdownOption) => {
    if (!option.isCustom) {
      return;
    }
    selectionSnapshotRef.current = { fromEmail, sender };
    const localPart = localPartFromEmail(option.id, domainName) || "";
    setFormMode("edit");
    setEditingOriginalEmail(option.id);
    setDraftLocalPart(localPart);
    setDraftDisplayName(option.sender);
    setFormErrors({});
    onChange({ fromEmail: option.id, sender: option.sender });
    onClearErrors?.(["fromEmail", "sender"]);
  };

  const handleDraftLocalPartChange = (value: string) => {
    setDraftLocalPart(value);
    syncDraftToParent(value, draftDisplayName);
    setFormErrors((prev) => ({ ...prev, localPart: undefined }));
  };

  const handleDraftDisplayNameChange = (value: string) => {
    const nextValue = stripSpaces(value);
    setDraftDisplayName(nextValue);
    if (formMode === "edit" && editingOriginalEmail) {
      onChange({ fromEmail: editingOriginalEmail, sender: nextValue });
    } else {
      syncDraftToParent(draftLocalPart, nextValue);
    }
    setFormErrors((prev) => ({ ...prev, displayName: undefined }));
  };

  const validateDraft = (): boolean => {
    const next: { localPart?: string; displayName?: string } = {};
    if (formMode === "create") {
      const local = draftLocalPart.trim();
      if (!local) {
        next.localPart = "Email username is required.";
      } else if (!USERNAME_PATTERN.test(local)) {
        next.localPart = "Use only letters, numbers, dots, hyphens, and underscores.";
      } else if (duplicateLocalPartError) {
        next.localPart = duplicateLocalPartError;
      }
    }
    if (!draftDisplayName.trim()) {
      next.displayName = "Sender display name is required.";
    }
    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validateDraft()) {
      return;
    }

    const displayName = draftDisplayName.trim();

    setSaving(true);
    try {
      if (formMode === "create") {
        const local = draftLocalPart.trim();
        const emailAddress = `${local}@${domainName}`;
        const result = await toastPromise(
          createEmailSenderUsername({
            emailAddress,
            displayName,
          }),
          {
            pending: "Creating custom from email in Azure...",
            success: "Custom from email created in Azure.",
            error: (error) =>
              error?.message || "Failed to create custom from email.",
          },
        );
        if (result.notice) {
          toastWarn(result.notice, { autoClose: 8000 });
        }
        exitForm(false);
        await refreshSenders(result.item?.emailAddress || emailAddress);
        return;
      }

      const emailAddress = editingOriginalEmail || "";
      const username = localPartFromEmail(emailAddress, domainName);
      if (!username) {
        toastError(`From email must belong to @${domainName}.`);
        return;
      }

      const result = await toastPromise(
        updateEmailSenderUsername({
          id: username,
          username,
          emailAddress,
          displayName,
        }),
        {
          pending: "Saving custom from email...",
          success: "Custom from email updated.",
          error: (error) =>
            error?.message || "Failed to update custom from email.",
        },
      );
      if (result.notice) {
        toastWarn(result.notice, { autoClose: 8000 });
      }
      exitForm(false);
      await refreshSenders(result.item?.emailAddress || emailAddress);
    } catch {
      // toastPromise already shows the error state.
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.isCustom) {
      return;
    }
    const username = localPartFromEmail(deleteTarget.id, domainName);
    if (!username) {
      toastError(`From email must belong to @${domainName}.`);
      return;
    }

    setDeleting(true);
    try {
      await deleteEmailSenderUsername(username);
      toastSuccess("Custom from email deleted.");
      if (notice) {
        toastWarn(notice, { autoClose: 8000 });
      }
      if (fromEmail === deleteTarget.id) {
        onChange({ fromEmail: "", sender: "" });
      }
      exitForm(false);
      setDeleteTarget(null);
      await refreshSenders();
    } catch (error: any) {
      toastError(error?.message || "Failed to delete custom from email.");
    } finally {
      setDeleting(false);
    }
  };

  const fromEmailCol =
    layout === "envelope" ? "col-span-12 md:col-span-4" : "w-full min-w-0 md:col-span-6";
  const fromNameCol =
    layout === "envelope" ? "col-span-12 md:col-span-4" : "w-full min-w-0 md:col-span-4";
  const gridClass =
    layout === "envelope"
      ? "grid grid-cols-12 gap-6"
      : "grid grid-cols-1 md:grid-cols-10 gap-6 w-full";

  const fromEmailField = formMode === "create" ? (
    <SenderEmailLocalPartInput
      title="From Email"
      name={layout === "envelope" ? "envelope-fromEmail" : "fromEmail"}
      value={draftLocalPart}
      domainName={domainName}
      onChange={handleDraftLocalPartChange}
      error={!!errors?.fromEmail || !!formErrors.localPart || !!duplicateLocalPartError}
      errorMsj={formErrors.localPart || duplicateLocalPartError || errors?.fromEmail}
      required
    />
  ) : formMode === "edit" ? (
    <SenderEmailLocalPartInput
      title="From Email"
      name={layout === "envelope" ? "envelope-fromEmail" : "fromEmail"}
      value={draftLocalPart}
      domainName={domainName}
      onChange={() => { }}
      error={!!errors?.fromEmail}
      errorMsj={errors?.fromEmail}
      required
      disabled
    />
  ) : (
    <FromEmailSenderDropdown
      title="From Email"
      name={layout === "envelope" ? "envelope-fromEmail" : "fromEmail"}
      options={options}
      selectedEmail={fromEmail}
      onSelect={handleSelectFromEmail}
      onEdit={handleStartEdit}
      onDelete={setDeleteTarget}
      onCreateNew={handleStartCreate}
      loading={loading}
      error={!!errors?.fromEmail}
      errormsj={errors?.fromEmail}
      required
      placeholder="Select from email"
    />
  );

  const fromNameField = (
    <Input
      title={layout === "envelope" ? "From" : "From Name"}
      name={layout === "envelope" ? "envelope-sender" : "sender"}
      required
      value={formMode === "select" ? sender : draftDisplayName}
      onChange={(event) => {
        if (formMode === "select") {
          return;
        }
        handleDraftDisplayNameChange(event.target.value);
      }}
      error={!!errors?.sender || !!formErrors.displayName}
      errorMsj={formErrors.displayName || errors?.sender}
      disabled={formMode === "select"}
    />
  );

  const fromNameHelpText = useMemo(() => {
    if (formMode === "create") {
      return "Enter the sender display name for the new address.";
    }
    if (formMode === "edit") {
      return "Editing display name for this custom sender.";
    }
    return "Set automatically by From Email selection.";
  }, [formMode]);

  return (
    <div className="flex flex-col gap-4">
      <div className={gridClass}>
        {layout === "envelope" ? (
          <>
            <div className={fromNameCol}>
              {fromNameField}
              <p className="text-sm text-gray-500 mt-1">{fromNameHelpText}</p>
            </div>
            <div className={fromEmailCol}>{fromEmailField}</div>
            {trailingFields}
          </>
        ) : (
          <>
            <div className={fromEmailCol}>{fromEmailField}</div>
            <div className={fromNameCol}>
              {fromNameField}
              <p className="text-sm text-gray-500 -mt-1.5">{fromNameHelpText}</p>
            </div>
          </>
        )}
      </div>

      {isEditingForm ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <LegacyButton
              type="button"
              text={saving ? "Saving…" : formMode === "create" ? "Create in Azure" : "Save changes"}
              onClick={handleSave}
              disabled={saving || deleting || !!duplicateLocalPartError}
              color="accent"
              size="small"
            />
            <LegacyButton
              type="button"
              text="Cancel"
              onClick={() => exitForm(true)}
              disabled={saving || deleting}
              color="neutral"
              style="outlined"
              size="small"
            />
          </div>
          <p className="text-sm text-gray-font font-bold">
            IMPORTANT: <span className="font-semibold text-primary">Changes may take a few minutes to become fully available for sending.</span>
          </p>
        </div>
      ) : null}

      {loadError ? (
        <p className="text-sm text-amber-700">
          {loadError} Showing built-in options until Azure senders can be loaded.
        </p>
      ) : null}



      <Modal
        title="Delete custom from email"
        isModalOpen={!!deleteTarget}
        toggleModal={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
        compact
        compactBodyScrolls={false}
        className="max-w-md p-6"
        outerPaddingOnly
      >
        <p className="text-base text-gray-font mb-6">
          Delete custom sender{" "}
          <span className="font-medium">{deleteTarget?.id}</span>? This cannot be undone.
        </p>
        <div className="flex flex-wrap justify-end gap-3">
          <LegacyButton
            type="button"
            text="Cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            color="neutral"
            style="outlined"
            size="small"
          />
          <LegacyButton
            type="button"
            text={deleting ? "Deleting…" : "Delete"}
            onClick={handleConfirmDelete}
            disabled={deleting}
            color="primary"
            size="small"
          />
        </div>
      </Modal>
    </div>
  );
}
